import { Outlet, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useSubscription } from '@/hooks/useSubscription'
import { Button } from '@/components/ui/Button'
import { Logo } from '@/components/ui/Logo'
import {
  LayoutDashboard,
  User,
  Users,
  Briefcase,
  Clock,
  Palette,
  LogOut,
  Menu,
  X,
  CalendarCheck,
  CalendarDays,
  BarChart3,
  CreditCard,
  Settings,
  MapPin,
  ChevronDown,
} from 'lucide-react'
import { useState } from 'react'
import { useProfileLocation } from '@/contexts/ProfileLocationContext'

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  requiredFeature?: string
  label?: string
  matchPrefix?: boolean
}

interface NavSection {
  sectionKey: string
  items: NavItem[]
}

const navigationSections: NavSection[] = [
  {
    sectionKey: 'nav.sections.operate',
    items: [
      { name: 'nav.dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'nav.schedule', href: '/dashboard/schedule', icon: CalendarDays },
      { name: 'nav.bookings', href: '/dashboard/bookings', icon: CalendarCheck },
      { name: 'nav.customers', href: '/dashboard/customers', icon: Users },
      {
        name: 'nav.analytics',
        href: '/dashboard/analytics',
        icon: BarChart3,
        requiredFeature: 'profile.analytics',
      },
    ],
  },
  {
    sectionKey: 'nav.sections.setup',
    items: [
      { name: 'nav.offerings', href: '/dashboard/offerings', icon: Briefcase, matchPrefix: true },
      { name: 'nav.hours', href: '/dashboard/hours', icon: Clock, matchPrefix: true },
      { name: 'nav.booking_settings', href: '/dashboard/booking-settings', icon: Settings },
    ],
  },
  {
    sectionKey: 'nav.sections.publicPage',
    items: [
      { name: 'nav.profile', href: '/dashboard/profile', icon: User, matchPrefix: true },
      { name: 'nav.branding', href: '/dashboard/branding', icon: Palette, matchPrefix: true },
    ],
  },
  {
    sectionKey: 'nav.sections.business',
    items: [
      { name: 'nav.team', href: '/dashboard/team', icon: Users },
      { name: 'nav.billing', href: '/dashboard/billing', icon: CreditCard, matchPrefix: true },
    ],
  },
]

export default function DashboardLayout() {
  const { t } = useTranslation('dashboard')
  const { user, logout, isLoading: authLoading } = useAuth()
  const { hasFeature, isLoading: subscriptionLoading } = useSubscription()
  const {
    locations,
    currentProfile,
    currentProfileId,
    setCurrentProfileId,
    isLoading: locationsLoading,
  } = useProfileLocation()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [locationMenuOpen, setLocationMenuOpen] = useState(false)

  const isActive = (item: NavItem) => {
    if (item.href === '/dashboard') {
      return location.pathname === '/dashboard'
    }
    if (item.matchPrefix) {
      return (
        location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
      )
    }
    return location.pathname === item.href
  }

  const isLoading = authLoading || subscriptionLoading || locationsLoading
  const showLocationSwitcher = locations.length > 0

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden">
        <button
          type="button"
          className="fixed top-4 left-4 z-50 p-2 bg-card rounded-lg shadow-md"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-border">
          <Logo className="text-xl" />
        </div>

        {/* Location switcher */}
        {showLocationSwitcher && (
          <div className="px-3 pb-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setLocationMenuOpen(!locationMenuOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border bg-background hover:bg-muted"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-left">
                    {currentProfile?.business_name || 'Select location'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
              </button>
              {locationMenuOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border bg-card shadow-lg py-1">
                  {locations.map((loc) => (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => {
                        setCurrentProfileId(loc.id)
                        setLocationMenuOpen(false)
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${
                        loc.id === currentProfileId ? 'text-primary font-medium' : 'text-foreground'
                      }`}
                    >
                      {loc.business_name}
                      {loc.is_primary ? ' · Primary' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="px-3 py-4 space-y-4 overflow-y-auto max-h-[calc(100vh-10rem)]">
          {navigationSections.map((section) => {
            const visibleItems = section.items.filter(
              (item) => !item.requiredFeature || hasFeature(item.requiredFeature),
            )
            if (visibleItems.length === 0) return null

            return (
              <div key={section.sectionKey}>
                <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t(section.sectionKey)}
                </p>
                <div className="space-y-1">
                  {visibleItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          isActive(item)
                            ? 'bg-primary text-white'
                            : 'text-foreground hover:bg-muted'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="w-5 h-5 mr-3" />
                        {t(item.name, { defaultValue: item.label || item.name })}
                      </Link>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt=""
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <span className="text-sm font-medium text-muted-foreground">
                    {user?.first_name?.[0] || user?.email?.[0] || 'U'}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.username || 'User'}
                </p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              disabled={isLoading}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-64">
        <div className="min-h-screen">
          <Outlet />
        </div>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}
